import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiHorizontalRule,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiInMemoryTable,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiStat,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import {
  OpsanioPangolinAppGap,
  OpsanioPangolinEndpoints,
  OpsanioPangolinResource,
  OpsanioPangolinTarget,
} from "src/questionnaires";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { FormSkeleton } from "src/components/skeleton";
import { useToast } from "src/notifications";
import { useOpsanioCan } from "../../can";

const i18n = "anketa.opsanio.appliance.flyout.pangolin";

/** Small read-only table of a resource's backend targets (ip:port). */
const TargetsTable = ({
  targets,
  labels,
}: {
  targets: OpsanioPangolinTarget[];
  labels: {
    endpoint: string;
    method: string;
    enabled: string;
    yes: string;
    no: string;
  };
}): JSX.Element => {
  const columns: Array<EuiBasicTableColumn<OpsanioPangolinTarget>> = [
    {
      field: "ip",
      name: labels.endpoint,
      render: (ip: string, t: OpsanioPangolinTarget) => (
        <EuiText size="xs">
          {ip || "-"}
          {t.port ? `:${t.port}` : ""}
        </EuiText>
      ),
    },
    {
      field: "method",
      name: labels.method,
      width: "120px",
      render: (method: string | null) => (
        <EuiText size="xs" color="subdued">
          {method ?? "-"}
        </EuiText>
      ),
    },
    {
      field: "enabled",
      name: labels.enabled,
      width: "90px",
      render: (enabled: boolean) => (
        <EuiBadge color={enabled ? "success" : "default"}>
          {enabled ? labels.yes : labels.no}
        </EuiBadge>
      ),
    },
  ];
  return (
    <EuiBasicTable<OpsanioPangolinTarget>
      items={targets}
      columns={columns}
      tableLayout="auto"
      compressed
    />
  );
};

export const OpsanioApplianceFlyoutPangolin = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();
  const can = useOpsanioCan();
  const canCreate = can(AnketaResourcesEnum.opsanioPangolin, "create", {
    isInternal: true,
  });

  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const siteName = instanceName === "" ? "" : `${instanceName}`;
  const [endpoints, setEndpoints] = useState<OpsanioPangolinEndpoints | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, React.ReactNode>>({});
  /** App (or `""` for the site) whose create request is in flight. */
  const [creating, setCreating] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName !== "",
    [keycloak, instanceName],
  );

  const getEndpoints = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setUnavailable(false);
    setLoadError(null);
    setExpanded({});
    try {
      const { success, data, error } = await Get<any>({
        path: `v2/${AnketaResourcesEnum.opsanio}/pangolin/endpoints/${encodeURIComponent(
          instanceName,
        )}`,
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        if ((error as any)?.status === 503) {
          setUnavailable(true);
        } else {
          setLoadError(
            (error as any)?.message ??
              translate(`${i18n}.error.body`, {
                defaultMessage: "Pangolin endpoints could not be loaded.",
              }),
          );
        }
        setEndpoints(null);
      } else {
        setEndpoints(((data as any)?.data as OpsanioPangolinEndpoints) ?? null);
      }
    } catch {
      setLoadError(
        translate(`${i18n}.error.body`, {
          defaultMessage: "Pangolin endpoints could not be loaded.",
        }),
      );
      setEndpoints(null);
    } finally {
      setLoading(false);
    }
    // `Get` is excluded on purpose: useApi() returns a new Get each render, so
    // including it would re-run this effect every render (infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, instanceName, keycloak, translate]);

  useEffect(() => {
    getEndpoints();
  }, [getEndpoints]);

  // Creates the site (`app` undefined) or one app's resource; the response is
  // the refreshed endpoint view, so no extra reload is needed.
  const create = useCallback(
    async (app?: string) => {
      if (!canFetch) return;
      setCreating(app ?? "");
      const base = `v2/${AnketaResourcesEnum.opsanio}/pangolin`;
      const name = encodeURIComponent(instanceName);
      try {
        const { success, data, error } = await Post<any>({
          path: app
            ? `${base}/endpoints/${name}/${encodeURIComponent(app)}`
            : `${base}/sites/${name}`,
          value: {},
          responseType: "json",
          token: keycloak.token,
        });
        if (!success || error) {
          addToast({
            title: translate(`${i18n}.create.error`, {
              defaultMessage: "Pangolin could not be updated",
            }),
            color: "danger",
            text: (error as any)?.service?.message ?? error?.message,
          });
          return;
        }
        addToast({
          title: app
            ? translate(`${i18n}.create.appDone`, {
                values: { app },
                defaultMessage: "Endpoint {{app}} created",
              })
            : translate(`${i18n}.create.siteDone`, {
                defaultMessage: "Pangolin site created",
              }),
          color: "success",
        });
        const next = (data as any)?.data as OpsanioPangolinEndpoints | undefined;
        if (next) setEndpoints(next);
        else void getEndpoints();
      } finally {
        setCreating(null);
      }
    },
    // `Post` is excluded for the same reason as `Get` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canFetch, instanceName, keycloak, translate, addToast, getEndpoints],
  );

  const targetLabels = useMemo(
    () => ({
      endpoint: translate(`${i18n}.column.endpoint`, {
        defaultMessage: "Endpoint",
      }),
      method: translate(`${i18n}.column.method`, { defaultMessage: "Method" }),
      enabled: translate(`${i18n}.column.enabled`, {
        defaultMessage: "Enabled",
      }),
      yes: translate("anketa.common.yes", { defaultMessage: "Yes" }),
      no: translate("anketa.common.no", { defaultMessage: "No" }),
    }),
    [translate],
  );

  const toggleExpand = useCallback(
    (resource: OpsanioPangolinResource) => {
      const id = String(resource.resourceId);
      setExpanded((prev) => {
        const next = { ...prev };
        if (next[id]) {
          delete next[id];
        } else {
          next[id] = (
            <TargetsTable targets={resource.targets} labels={targetLabels} />
          );
        }
        return next;
      });
    },
    [targetLabels],
  );

  const columns: Array<EuiBasicTableColumn<OpsanioPangolinResource>> = [
    {
      field: "app",
      name: translate(`${i18n}.column.app`, { defaultMessage: "App" }),
      sortable: true,
      width: "130px",
      render: (app: string) => <EuiBadge color="hollow">{app}</EuiBadge>,
    },
    {
      field: "name",
      name: translate(`${i18n}.column.resource`, {
        defaultMessage: "Resource",
      }),
      sortable: true,
      render: (name: string) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type="globe" size="s" color="subdued" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">{name}</EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      field: "fullDomain",
      name: translate(`${i18n}.column.domain`, { defaultMessage: "Domain" }),
      render: (domain: string | null) =>
        domain ? (
          <EuiLink href={`https://${domain}`} target="_blank" external>
            {domain}
          </EuiLink>
        ) : (
          <EuiText size="xs" color="subdued">
            -
          </EuiText>
        ),
    },
    {
      field: "protocol",
      name: translate(`${i18n}.column.protocol`, {
        defaultMessage: "Protocol",
      }),
      width: "100px",
      render: (protocol: string | null) => (
        <EuiText size="xs" color="subdued">
          {protocol ?? "-"}
        </EuiText>
      ),
    },
    {
      field: "enabled",
      name: translate(`${i18n}.column.enabled`, { defaultMessage: "Enabled" }),
      width: "90px",
      render: (enabled: boolean) => (
        <EuiBadge color={enabled ? "success" : "default"}>
          {enabled ? targetLabels.yes : targetLabels.no}
        </EuiBadge>
      ),
    },
    {
      name: translate(`${i18n}.column.targets`, { defaultMessage: "Targets" }),
      width: "110px",
      render: (resource: OpsanioPangolinResource) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiBadge color="hollow">{resource.targets.length}</EuiBadge>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              aria-label={translate(`${i18n}.column.targets`, {
                defaultMessage: "Targets",
              })}
              iconType={
                expanded[String(resource.resourceId)] ? "arrowUp" : "arrowDown"
              }
              onClick={() => toggleExpand(resource)}
              isDisabled={resource.targets.length === 0}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
  ];

  const header = (
    <EuiFlexGroup
      justifyContent="spaceBetween"
      alignItems="center"
      gutterSize="s"
      responsive={false}
    >
      <EuiFlexItem>
        <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type="link" size="l" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiTitle size="s">
              <h3 style={{ margin: 0 }}>
                {translate(`${i18n}.title`, {
                  defaultMessage: "Pangolin endpoints",
                })}
              </h3>
            </EuiTitle>
          </EuiFlexItem>
          {siteName !== "" && (
            <EuiFlexItem grow={false}>
              <EuiBadge color="hollow">{siteName}</EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="refresh"
          onClick={getEndpoints}
          isLoading={loading}
          disabled={!canFetch}
        >
          {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const wrap = (body: React.ReactNode) => (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      {header}
      <EuiHorizontalRule margin="m" />
      {body}
    </EuiPanel>
  );

  if (instanceName === "") {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="help"
        title={translate(`${i18n}.noInstance.title`, {
          defaultMessage: "No site identifier",
        })}
      >
        <p>
          {translate(`${i18n}.noInstance.body`, {
            defaultMessage:
              "This appliance has no VPC name (common.metadata.name), so no Pangolin site can be resolved.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loading) return wrap(<FormSkeleton />);

  if (unavailable) {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="iInCircle"
        title={translate(`${i18n}.unavailable.title`, {
          defaultMessage: "Pangolin not configured",
        })}
      >
        <p>
          {translate(`${i18n}.unavailable.body`, {
            defaultMessage:
              "The Pangolin Integration API connection is not configured on the server.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loadError) {
    return wrap(
      <EuiCallOut
        color="danger"
        iconType="alert"
        title={translate(`${i18n}.error.title`, {
          defaultMessage: "Endpoints unavailable",
        })}
      >
        <p>{loadError}</p>
        <EuiButton
          size="s"
          color="danger"
          iconType="refresh"
          onClick={getEndpoints}
        >
          {translate("anketa.common.retry", { defaultMessage: "Retry" })}
        </EuiButton>
      </EuiCallOut>,
    );
  }

  if (endpoints && !endpoints.siteFound) {
    return wrap(
      <EuiEmptyPrompt
        iconType="link"
        title={
          <h3>
            {translate(`${i18n}.noSite.title`, {
              defaultMessage: "No Pangolin site found",
            })}
          </h3>
        }
        body={
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.noSite.body`, {
              values: { site: endpoints.site },
              defaultMessage:
                'No Pangolin site "{{site}}" exists in the organization yet.',
            })}
          </EuiText>
        }
        actions={
          canCreate ? (
            <EuiButton
              size="s"
              iconType="plusInCircle"
              onClick={() => void create()}
              isLoading={creating === ""}
              disabled={creating !== null}
            >
              {translate(`${i18n}.create.site`, {
                defaultMessage: "Create site",
              })}
            </EuiButton>
          ) : undefined
        }
      />,
    );
  }

  const gapRow = (gap: OpsanioPangolinAppGap) => (
    <EuiFlexGroup
      key={gap.app}
      gutterSize="s"
      alignItems="center"
      responsive={false}
    >
      <EuiFlexItem grow={false}>
        <EuiBadge color="hollow">{gap.app}</EuiBadge>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size="xs" color="subdued">
          {translate(`${i18n}.gaps.fleetSites`, {
            values: { count: gap.fleetSites },
            defaultMessage: "on {{count}} other sites",
          })}
          {gap.plannedTarget
            ? ` · ${gap.plannedTarget.ip}${
                gap.plannedTarget.port ? `:${gap.plannedTarget.port}` : ""
              }`
            : ""}
        </EuiText>
      </EuiFlexItem>
      {canCreate && (
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            iconType="plusInCircle"
            onClick={() => void create(gap.app)}
            isLoading={creating === gap.app}
            disabled={creating !== null || gap.plannedTarget === null}
          >
            {translate(`${i18n}.create.app`, { defaultMessage: "Create" })}
          </EuiButton>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  const missing = endpoints?.missing ?? [];
  const available = endpoints?.available ?? [];
  const gaps =
    missing.length + available.length === 0 ? null : (
      <>
        <EuiSpacer size="m" />
        {missing.length > 0 && (
          <EuiCallOut
            color="warning"
            iconType="alert"
            size="s"
            title={translate(`${i18n}.gaps.missing`, {
              defaultMessage: "Missing compared to the fleet",
            })}
          >
            {missing.map(gapRow)}
          </EuiCallOut>
        )}
        {missing.length > 0 && available.length > 0 && <EuiSpacer size="s" />}
        {available.length > 0 && (
          <EuiCallOut
            color="primary"
            iconType="iInCircle"
            size="s"
            title={translate(`${i18n}.gaps.available`, {
              defaultMessage: "Further apps other sites publish",
            })}
          >
            {available.map(gapRow)}
          </EuiCallOut>
        )}
      </>
    );

  if (endpoints && endpoints.resources.length === 0) {
    return wrap(
      <>
      <EuiEmptyPrompt
        iconType="globe"
        title={
          <h3>
            {translate(`${i18n}.empty.title`, {
              defaultMessage: "No endpoints exposed",
            })}
          </h3>
        }
        body={
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.empty.body`, {
              defaultMessage:
                "This site has no resources exposed through Pangolin.",
            })}
          </EuiText>
        }
      />
      {gaps}
      </>,
    );
  }

  return wrap(
    <>
      <EuiFlexGroup gutterSize="l" wrap>
        <EuiFlexItem>
          <EuiStat
            title={`${endpoints?.resourceCount ?? 0}`}
            description={translate(`${i18n}.stat.resources`, {
              defaultMessage: "Resources",
            })}
            titleSize="m"
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            title={`${endpoints?.targetCount ?? 0}`}
            description={translate(`${i18n}.stat.targets`, {
              defaultMessage: "Targets",
            })}
            titleSize="m"
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      <EuiInMemoryTable<OpsanioPangolinResource>
        items={endpoints?.resources ?? []}
        columns={columns}
        itemId="resourceId"
        itemIdToExpandedRowMap={expanded}
        sorting={{ sort: { field: "app", direction: "asc" } }}
        pagination={{ initialPageSize: 10, pageSizeOptions: [10, 25, 50] }}
        rowHeader="name"
      />
      {gaps}
    </>,
  );
};

export default OpsanioApplianceFlyoutPangolin;
